import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const productNavItems = [
  { path: '/markets', label: 'Markets' },
  { path: '/ai-agents', label: 'AI Agents' },
  { path: '/vaults', label: 'Vaults' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/fees', label: 'Fees' },
];

const traders = [
  { rank: 1, name: 'AlphaWhale', pnl30d: 245.6, winRate: 89, trades: 3420, volume: 125000000, followers: 12450 },
  { rank: 2, name: 'QuantumBot', pnl30d: 198.3, winRate: 84, trades: 5670, volume: 98000000, followers: 8900 },
  { rank: 3, name: 'MoonShot_7', pnl30d: 176.8, winRate: 78, trades: 2100, volume: 67000000, followers: 5600 },
  { rank: 4, name: 'DeFiWizard', pnl30d: 154.2, winRate: 82, trades: 1890, volume: 54000000, followers: 4300 },
  { rank: 5, name: 'ArbitrageKing', pnl30d: 132.7, winRate: 91, trades: 4560, volume: 42000000, followers: 3200 },
  { rank: 6, name: 'VolatilityHunter', pnl30d: 118.5, winRate: 75, trades: 6780, volume: 38000000, followers: 2800 },
  { rank: 7, name: 'CryptoNinja', pnl30d: 105.3, winRate: 71, trades: 2340, volume: 31000000, followers: 2100 },
  { rank: 8, name: 'BlockChainPro', pnl30d: 98.7, winRate: 68, trades: 1560, volume: 28000000, followers: 1900 },
  { rank: 9, name: 'YieldFarmer_X', pnl30d: 87.4, winRate: 79, trades: 3450, volume: 22000000, followers: 1500 },
  { rank: 10, name: 'SignalMaster', pnl30d: 76.2, winRate: 73, trades: 2890, volume: 19000000, followers: 1200 },
];

function formatVolume(n: number) {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${(n / 1e3).toFixed(1)}K`;
}

export default function LeaderboardPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('leaderboard.title', 'Leaderboard')}
      subtitle={t('leaderboard.subtitle', 'Top-performing traders and AI agents ranked by 30-day PnL')}
      category="Product"
      navItems={productNavItems}
    >
      <div className={`rounded-xl border ${borderLight} ${bgCard} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${borderLight} ${textMuted}`}>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.rank', 'Rank')}</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.trader', 'Trader')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.pnl30d', 'PNL (30d)')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.winRate', 'Win Rate')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.trades', 'Trades')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.volume', 'Volume')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.followers', 'Followers')}</th>
              </tr>
            </thead>
            <tbody>
              {traders.map((tr) => (
                <tr key={tr.rank} className={`border-b ${borderLight} last:border-0 hover:bg-[#00ff88]/5 transition-colors`}>
                  <td className={`px-4 py-3 font-mono font-bold ${tr.rank <= 3 ? 'text-[#00ff88]' : textMuted}`}>#{tr.rank}</td>
                  <td className={`px-4 py-3 font-medium ${textPrimary}`}>{tr.name}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#00ff88]">+{tr.pnl30d}%</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{tr.winRate}%</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{tr.trades.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{formatVolume(tr.volume)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>{tr.followers.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CompanyLayout>
  );
}